import { Button } from 'components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from 'components/shadcn/dropdown-menu';
import { cn } from 'utils';
import { Columns3, GripVertical } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface ConversationColumnOption {
  id: string;
  label: string;
}

interface Props {
  columns: ConversationColumnOption[];
  isColumnVisible: (columnId: string) => boolean;
  onColumnVisibilityChange: (columnId: string, visible: boolean) => void;
  onColumnOrderChange: (draggedColumnId: string, targetColumnId: string) => void;
}

export function ConversationColumnVisibility({
  columns,
  isColumnVisible,
  onColumnVisibilityChange,
  onColumnOrderChange,
}: Props) {
  const { t } = useTranslation();
  const [draggedColumnId, setDraggedColumnId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const visibleColumnCount = columns.filter(({ id }) => isColumnVisible(id)).length;

  const clearDragState = () => {
    setDraggedColumnId(null);
    setDragOverColumnId(null);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-2">
          <Columns3 className="h-4 w-4" data-icon="inline-start" />
          {t('dashboard.conversations.columns.button')}
          <span className="text-xs text-muted-foreground">
            {visibleColumnCount}/{columns.length}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-56">
        <DropdownMenuLabel>{t('dashboard.conversations.columns.reorderHint')}</DropdownMenuLabel>
        {columns.map(({ id, label }) => (
          <DropdownMenuCheckboxItem
            key={id}
            checked={isColumnVisible(id)}
            draggable
            className={cn(
              'cursor-grab active:cursor-grabbing',
              dragOverColumnId === id && 'bg-accent',
            )}
            onCheckedChange={(visible) => onColumnVisibilityChange(id, visible)}
            onDragStart={(event) => {
              event.dataTransfer.effectAllowed = 'move';
              event.dataTransfer.setData('text/plain', id);
              setDraggedColumnId(id);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (draggedColumnId !== id) setDragOverColumnId(id);
            }}
            onDrop={(event) => {
              event.preventDefault();
              const sourceId = event.dataTransfer.getData('text/plain');
              if (sourceId && sourceId !== id) onColumnOrderChange(sourceId, id);
              clearDragState();
            }}
            onDragEnd={clearDragState}
          >
            <GripVertical className="me-1 size-4 text-muted-foreground" aria-hidden="true" />
            {label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
